import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Plus, Edit, Trash2, ExternalLink, Key, Loader2 } from 'lucide-react';
import { apiService, LLMProvider, LLMParameterDefinition, LLMProviderDriverType } from '../../services/api';
import { ParameterEditor } from '../ParameterEditor';

const DRIVER_TYPES: { value: LLMProviderDriverType; label: string }[] = [
  { value: 'openai', label: 'OpenAI (Standard)' },
  { value: 'azure-openai', label: 'Azure OpenAI' },
  { value: 'openai-compatible', label: 'OpenAI-kompatibel' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'grok', label: 'xAI Grok' },
];


export function LLMProvidersPage() {
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LLMProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    name: '',
    displayName: '',
    description: '',
    baseUrl: '',
    driverType: 'azure-openai' as LLMProviderDriverType,
    dataResidency: '',
    apiKey: '',
    // Responses-API (Reasoning-Modelle, CA-825) braucht auf Azure mindestens diese Version.
    apiVersion: '2025-04-01-preview',
    apiKeyRequired: true,
    supportedParameters: [] as LLMParameterDefinition[],
    additionalConfig: '{}'
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLlmProviders(true);
      setProviders(data);
    } catch (err) {
      console.error('Failed to load providers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    // Simple notification implementation
    console.log(`${type.toUpperCase()}: ${message}`);
    // In a real app, you'd use a proper notification system
  };

  const filteredProviders = providers.filter(provider =>
    provider.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    provider.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (provider: LLMProvider) => {
    setEditingProvider(provider);
    const { supportedParameters, apiVersion, ...restConfig } = provider.configuration || {};
    setFormData({
      name: provider.name,
      displayName: provider.displayName,
      description: provider.description || '',
      baseUrl: provider.baseUrl || '',
      driverType: provider.driverType || 'azure-openai',
      dataResidency: provider.dataResidency || '',
      apiKey: '', // never returned by the API; leave blank to keep the stored key
      apiVersion: apiVersion || '',
      apiKeyRequired: provider.apiKeyRequired,
      supportedParameters: supportedParameters || [],
      additionalConfig: JSON.stringify(restConfig || {}, null, 2)
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.displayName) {
      showNotification('Name und Anzeigename sind erforderlich', 'error');
      return;
    }

    try {
      setSaving(true);
      const additionalConfigObj = JSON.parse(formData.additionalConfig);
      const configObj = {
        ...additionalConfigObj,
        supportedParameters: formData.supportedParameters
      };

      // Shared payload; apiKey only sent when the admin entered a (new) value.
      const basePayload = {
        name: formData.name,
        displayName: formData.displayName,
        description: formData.description,
        baseUrl: formData.baseUrl,
        driverType: formData.driverType,
        dataResidency: formData.dataResidency || undefined,
        apiVersion: formData.apiVersion || undefined,
        apiKeyRequired: formData.apiKeyRequired,
        configuration: configObj,
        ...(formData.apiKey ? { apiKey: formData.apiKey } : {}),
      };

      if (editingProvider) {
        const updatedProvider = await apiService.updateLlmProvider(editingProvider.id, basePayload);
        setProviders(providers.map(p => p.id === editingProvider.id ? updatedProvider : p));
        showNotification('Provider erfolgreich aktualisiert');
      } else {
        const newProvider = await apiService.createLlmProvider(basePayload);
        setProviders([...providers, newProvider]);
        showNotification('Provider erfolgreich erstellt');
      }

      setIsDialogOpen(false);
      setEditingProvider(null);
      setFormData(emptyForm);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      if (errorMessage.includes('JSON')) {
        showNotification('Ungültige JSON-Konfiguration', 'error');
      } else {
        showNotification(`Fehler beim Speichern: ${errorMessage}`, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    if (confirm(`Sind Sie sicher, dass Sie den Provider "${provider.displayName}" löschen möchten?`)) {
      try {
        await apiService.deleteLlmProvider(id);
        setProviders(providers.filter(p => p.id !== id));
        showNotification('Provider erfolgreich gelöscht');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
        showNotification(`Fehler beim Löschen: ${errorMessage}`, 'error');
      }
    }
  };

  const toggleActive = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    try {
      const newStatus = provider.status === 'active' ? 'inactive' : 'active';
      const updatedProvider = await apiService.updateLlmProvider(id, {
        status: newStatus as 'active' | 'inactive' | 'deprecated',
      });
      setProviders(providers.map(p => p.id === id ? updatedProvider : p));
      showNotification(`Provider ${newStatus === 'active' ? 'aktiviert' : 'deaktiviert'}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      showNotification(`Fehler beim Ändern des Status: ${errorMessage}`, 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LLM Provider</h1>
          <p className="text-gray-600">Verwaltung der Sprachmodell-Anbieter</p>
        </div>
        <Button
          onClick={() => { setEditingProvider(null); setFormData(emptyForm); setIsDialogOpen(true); }}
          disabled={loading}
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Provider
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Provider Übersicht</CardTitle>
              <CardDescription>{providers.length} Provider konfiguriert</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Provider suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading providers...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{typeof error === 'string' ? error : (error?.message || 'An error occurred')}</p>
              <Button onClick={loadProviders} variant="outline">
                Retry
              </Button>
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No providers found matching your search' : 'No providers configured yet'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProviders.map((provider) => (
              <div key={provider.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{provider.displayName}</h3>
                      <Badge variant={provider.status === 'active' ? 'default' : 'secondary'}>
                        {provider.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                      {provider.dataResidency && (
                        <Badge className="text-xs font-semibold uppercase bg-blue-600 text-white border-transparent hover:bg-blue-600">
                          {provider.dataResidency}
                        </Badge>
                      )}
                      {provider.apiKeyRequired && (
                        <Badge variant="outline" className="text-orange-700 border-orange-200">
                          <Key className="w-3 h-3 mr-1" />
                          API Key
                        </Badge>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{provider.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <ExternalLink className="w-3 h-3" />
                        <span>{provider.baseUrl || 'No URL'}</span>
                      </div>
                      <div>{provider.modelsCount || 0} Modelle</div>
                      <div>Erstellt: {new Date(provider.createdAt).toLocaleDateString('de-DE')}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={provider.status === 'active'}
                      onCheckedChange={() => toggleActive(provider.id)}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(provider.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? 'Provider bearbeiten' : 'Neuen Provider erstellen'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie die Einstellungen für den LLM Provider
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name (technisch)</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="z.B. openai"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Anzeigename</Label>
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                  placeholder="z.B. OpenAI"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschreibung des Providers..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driverType">Treiber-Typ</Label>
                <Select
                  value={formData.driverType}
                  onValueChange={(value) => setFormData({...formData, driverType: value as LLMProviderDriverType})}
                >
                  <SelectTrigger id="driverType">
                    <SelectValue placeholder="Treiber wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {DRIVER_TYPES.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataResidency">Daten-Region (data residency)</Label>
                <Input
                  id="dataResidency"
                  value={formData.dataResidency}
                  maxLength={20}
                  onChange={(e) => setFormData({...formData, dataResidency: e.target.value})}
                  placeholder="z.B. eu, us"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseUrl">API URL / Endpoint</Label>
              <Input
                id="baseUrl"
                value={formData.baseUrl}
                onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                placeholder="https://api.example.com"
              />
            </div>
            {formData.driverType === 'azure-openai' && (
              <div className="space-y-2">
                <Label htmlFor="apiVersion">Azure API-Version</Label>
                <Input
                  id="apiVersion"
                  value={formData.apiVersion}
                  onChange={(e) => setFormData({...formData, apiVersion: e.target.value})}
                  placeholder="z.B. 2025-04-01-preview"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                type="password"
                value={formData.apiKey}
                onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
                placeholder={editingProvider?.hasApiKey ? '•••••••• (gespeichert — leer lassen zum Beibehalten)' : 'API Key eingeben'}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500">Wird verschlüsselt gespeichert und nie zurückgegeben.</p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="apiKeyRequired"
                checked={formData.apiKeyRequired}
                onCheckedChange={(checked) => setFormData({...formData, apiKeyRequired: checked})}
              />
              <Label htmlFor="apiKeyRequired">API Key erforderlich</Label>
            </div>

            <div className="border-t pt-4">
              <ParameterEditor
                parameters={formData.supportedParameters}
                onChange={(parameters) => setFormData({...formData, supportedParameters: parameters})}
                title="Unterstützte Parameter"
                description="Definieren Sie die globalen Parameter für alle Modelle dieses Providers"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionalConfig">Zusätzliche Konfiguration (JSON)</Label>
              <Textarea
                id="additionalConfig"
                value={formData.additionalConfig}
                onChange={(e) => setFormData({...formData, additionalConfig: e.target.value})}
                placeholder='{"rateLimits": {"requestsPerMinute": 60}}'
                className="min-h-24"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingProvider ? 'Speichern...' : 'Erstellen...'}
                  </>
                ) : (
                  editingProvider ? 'Speichern' : 'Erstellen'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}